import * as types from '@/store/types';
import jwtDecode from 'jwt-decode';
import { AccountRole } from '@/entities/accounts/account-role.enum';
import { IAuthToken } from '@/entities/authToken.interface';
import { Permission, MeModel } from '@/entities';
import authService from '@/services/auth.service';
import accountsApi from '@/api/accounts.api';
import store from '@/store/store';

function getFromToken(token, key) {
    if (!token) {
        return null;
    }
    return token[key];
}

export default {
    state: {
        token: authService.getToken(),
        me: authService.getMe()
    },
    getters: {
        [types.AUTH_GET_IS_LOGGED]: (state) => {
            return state.token != null;
        },
        decodedToken: (state): IAuthToken => {
            if (!state.token) {
                return null;
            }
            return jwtDecode(state.token);
        },
        [types.AUTH_GET_ME]: (state) => {
            return state.me;
        },
        [types.AUTH_GET_ACCOUNT_ID]: (state, getters) => {
            return getFromToken(getters.decodedToken, 'accountId');
        },
        [types.AUTH_GET_AGENT_ID]: (state, getters) => {
            return getFromToken(getters.decodedToken, 'agentId');
        },
        [types.AUTH_GET_COMPANY_ID]: (state, getters) => {
            return getFromToken(getters.decodedToken, 'companyId');
        },
        // Roles
        [types.AUTH_GET_ROLE]: (state, getters) => {
            return getFromToken(getters.decodedToken, 'role');
        },
        [types.AUTH_HAS_ROLE_PERMISSIONS]: (state, getters) => (
            role: AccountRole
        ) => {
            const authRole = getters[types.AUTH_GET_ROLE];
            if (authRole === null) {
                return false;
            }
            return role >= authRole;
        },
        [types.AUTH_HAS_COMPANY_PERMISSIONS]: (state, getters) => {
            return getters[types.AUTH_HAS_ROLE_PERMISSIONS](
                AccountRole.Company
            );
        },
        [types.AUTH_HAS_ADMIN_PERMISSIONS]: (state, getters) => {
            return getters[types.AUTH_HAS_ROLE_PERMISSIONS](AccountRole.Admin);
        },
        [types.AUTH_HAS_SUPERADMIN_PERMISSIONS]: (state, getters) => {
            return getters[types.AUTH_HAS_ROLE_PERMISSIONS](
                AccountRole.Superadmin
            );
        },
        // Permissions
        [types.AUTH_HAS_PERMISSION]: (state, getters) => (
            permission: Permission
        ) => {
            const meModel: MeModel = getters[types.AUTH_GET_ME];
            if (getters[types.AUTH_HAS_SUPERADMIN_PERMISSIONS]) {
                return true;
            }
            if (
                !meModel ||
                !meModel.permissions ||
                !meModel.permissions.length
            ) {
                return false;
            }
            return meModel.permissions.find((x) => x === permission) !== null;
        },
        [types.AUTH_HAS_PERMISSION_APPROVE_AGENTS]: (state, getters) => {
            return getters[types.AUTH_HAS_PERMISSION](
                Permission.APPROVE_AGENTS
            );
        },
        [types.AUTH_HAS_PERMISSION_JESSE]: (state, getters) => {
            const meModel: MeModel = getters[types.AUTH_GET_ME];
            return meModel.username === process.env.VUE_APP_JESSE_LOGIN;
        },
    },
    mutations: {
        [types.AUHT_LOGIN_SUCCESS](state, token) {
            state.token = token;
        },
        [types.AUTH_LOGOUT_SUCCESS](state) {
            state.token = null;
            state.me = null;
        },
        [types.AUTH_SET_ME_MODEL](state, meModel) {
            state.me = meModel;
        }
    },
    actions: {
        [types.AUHT_LOGIN]: (context, token) => {
            authService.storeToken(token);
            context.commit(types.AUHT_LOGIN_SUCCESS, token);
            accountsApi.me().then((meModel: MeModel) => {
                context.dispatch(types.AUTH_STORE_ME, meModel);
            });
            context.dispatch(types.APPROVE_START_TIMER_UNAPPROVED_COUNT);
        },
        [types.AUTH_LOGOUT]: (context) => {
            authService.clear();
            context.commit(types.AUTH_LOGOUT_SUCCESS);
            context.dispatch(types.APPROVE_END_TIMER_UNAPPROVED_COUNT);
        },
        [types.AUTH_STORE_ME]: (context, meModel) => {
            authService.storeMe(meModel);
            context.commit(types.AUTH_SET_ME_MODEL, meModel);
        },
    },
};
